import {
  CONFIGURED_ENVIRONMENTS,
  DEFAULT_ENVIRONMENT,
  ENVIRONMENT_TARGETS,
  env,
  type MeshEnvironment,
} from '@/lib/env';

/** Survives the full page load that Google SSO bounces the operator through. */
const ENVIRONMENT_KEY = 'mesh-admin:environment';

/**
 * Guard against the console and Google trading redirects forever. Lives here
 * rather than in the API client because switching environments must clear it:
 * the flag means "we already tried to sign in", which is only true of the
 * environment it was set against.
 */
export const SIGN_IN_ATTEMPTED_KEY = 'mesh-admin:sign-in-attempted';

/**
 * Pure so it can be tested without reloading the module graph.
 *
 * A stored value is honoured only if this build can still reach it. Environments
 * come and go between deploys, and a console that boots pointed at a target it
 * has no URL for would either crash or — far worse — silently fall back while
 * still showing the stored environment's badge.
 */
export function resolveEnvironment(
  stored: string | null,
  configured: readonly MeshEnvironment[],
  fallback: MeshEnvironment,
): MeshEnvironment {
  const match = configured.find((environment) => environment === stored);
  return match ?? fallback;
}

function readStoredEnvironment(): string | null {
  try {
    return localStorage.getItem(ENVIRONMENT_KEY);
  } catch {
    // Safari private mode and locked-down enterprise profiles throw on access.
    // A console that cannot remember the choice is fine; one that will not boot
    // is not.
    return null;
  }
}

/**
 * The environment this page load is pointed at.
 *
 * Deliberately a constant rather than reactive state. Switching environments
 * reloads the page (see `switchEnvironment`), so nothing can observe this
 * change mid-session — which is the property that makes the badge trustworthy.
 */
export const activeEnvironment: MeshEnvironment = resolveEnvironment(
  readStoredEnvironment(),
  CONFIGURED_ENVIRONMENTS,
  DEFAULT_ENVIRONMENT,
);

/** Base URL of the mesh-admin-api this page load talks to. */
export const activeApiUrl: string =
  ENVIRONMENT_TARGETS[activeEnvironment] ??
  (ENVIRONMENT_TARGETS[DEFAULT_ENVIRONMENT] as string);

export const isProduction: boolean = activeEnvironment === 'production';

/**
 * Mocks are gated on the active environment, not on the build mode.
 *
 * The rule that matters is "never fabricate data while pointed at production" —
 * an operator must never act on invented state. Gating on `import.meta.env.DEV`
 * instead would also disable mocks in the e2e suite, which runs a production
 * build against a staging/dev target and legitimately needs them.
 */
export const useMocks: boolean = env.useMocksRequested && !isProduction;

/** True when this build has more than one reachable target to offer. */
export const canSwitchEnvironments: boolean = CONFIGURED_ENVIRONMENTS.length > 1;

export function environmentApiUrl(environment: MeshEnvironment): string | null {
  return ENVIRONMENT_TARGETS[environment];
}

/**
 * Point the console at a different environment.
 *
 * Always a full reload, never an in-place swap. A React re-render would leave
 * the TanStack Query cache full of the previous environment's rows, an MSW
 * worker running that must not run against production, and in-flight requests
 * landing against the old base URL — staging data rendered under a Production
 * badge is precisely the confusion the badge exists to prevent. Throwing the
 * whole document away is the only version of this that is honest.
 */
export function switchEnvironment(next: MeshEnvironment): void {
  if (next === activeEnvironment) return;
  if (ENVIRONMENT_TARGETS[next] === null) return;

  try {
    localStorage.setItem(ENVIRONMENT_KEY, next);
    // The new environment has its own session cookie and its own admin_users
    // table, so the operator gets one fresh sign-in attempt against it.
    sessionStorage.removeItem(SIGN_IN_ATTEMPTED_KEY);
  } catch {
    // Storage is unavailable, so the choice cannot persist across the reload.
    // Reloading anyway would silently drop the operator back where they were.
    throw new Error(
      'Could not switch environment: this browser is blocking site storage, so the choice would not survive the reload.',
    );
  }

  window.location.reload();
}
